import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  IconButton,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import GroupIcon from "@material-ui/icons/Group";
import EditIcon from "@material-ui/icons/Edit";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(1),
  },
  titleIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  groupHeader: {
    textAlign: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  groupName: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  groupInfo: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  infoChip: {
    margin: theme.spacing(0.5),
  },
  participantsList: {
    maxHeight: "400px",
    overflow: "auto",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  actionButton: {
    flex: 1,
    minWidth: "120px",
  },
}));

const GroupInfoModal = ({ open, onClose, contact, ticket }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (open && contact?.isGroup && contact?.id) {
      fetchGroupParticipants();
      setNewName(contact?.name || "");
      setNewDescription(contact?.description || "");
    }
  }, [open, contact]);

  const fetchGroupParticipants = async () => {
    setLoading(true);
    try {
      // Usar o endpoint correto ou dados do contact
      if (contact?.participants) {
        setParticipants(contact.participants);
      } else {
        setParticipants([]);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!newName.trim()) {
      toast.error("Nome do grupo não pode estar vazio");
      return;
    }
    if (!contact?.remoteJid || !ticket?.whatsappId) {
      toast.error("Dados do grupo incompletos");
      console.log('[GroupInfoModal] Dados faltando:', { remoteJid: contact?.remoteJid, whatsappId: ticket?.whatsappId });
      return;
    }
    setActionLoading(true);
    try {
      console.log('[GroupInfoModal] Atualizando nome:', { groupJid: contact?.remoteJid, newSubject: newName, whatsappId: ticket?.whatsappId });
      await api.put("/groups/update-name", {
        groupJid: contact?.remoteJid,
        newSubject: newName,
        whatsappId: ticket?.whatsappId,
      });
      toast.success("Nome do grupo atualizado com sucesso!");
      setEditingName(false);
    } catch (err) {
      console.error('[GroupInfoModal] Erro ao atualizar nome:', err);
      toastError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateDescription = async () => {
    if (!contact?.remoteJid || !ticket?.whatsappId) {
      toast.error("Dados do grupo incompletos");
      return;
    }
    setActionLoading(true);
    try {
      await api.put("/groups/update-description", {
        groupJid: contact?.remoteJid,
        description: newDescription,
        whatsappId: ticket?.whatsappId,
      });
      toast.success("Descrição do grupo atualizada com sucesso!");
      setEditingDescription(false);
    } catch (err) {
      toastError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePicture = async () => {
    if (!contact?.remoteJid || !ticket?.whatsappId) {
      toast.error("Dados do grupo incompletos");
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append("picture", file);
        formData.append("groupJid", contact?.remoteJid);
        formData.append("whatsappId", ticket?.whatsappId);

        await api.put("/groups/update-picture", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Foto do grupo atualizada com sucesso!");
      } catch (err) {
        toastError(err);
      } finally {
        setActionLoading(false);
      }
    };
    input.click();
  };

  const handleLeaveGroup = async () => {
    if (!contact?.remoteJid || !ticket?.whatsappId) {
      toast.error("Dados do grupo incompletos");
      return;
    }
    if (!window.confirm("Tem certeza que deseja sair do grupo?")) return;

    setActionLoading(true);
    try {
      await api.post("/groups/leave", {
        groupJid: contact?.remoteJid,
        whatsappId: ticket?.whatsappId,
      });
      toast.success("Você saiu do grupo com sucesso!");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Dialog open={open && contact?.isGroup} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <GroupIcon className={classes.titleIcon} />
          <Typography variant="subtitle1">Informações do Grupo</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Group Header */}
        <Box className={classes.groupHeader}>
          {editingName ? (
            <TextField
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={handleUpdateName}
              autoFocus
              size="small"
              disabled={actionLoading}
            />
          ) : (
            <Typography className={classes.groupName}>
              {contact?.name || "Grupo"}
            </Typography>
          )}
          <Box className={classes.groupInfo}>
            <Chip
              label={`${participants.length} participantes`}
              color="primary"
              variant="outlined"
              className={classes.infoChip}
            />
            <Chip
              label={`ID: ${contact?.number || "N/A"}`}
              variant="outlined"
              className={classes.infoChip}
            />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box className={classes.actionButtons}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setEditingName(!editingName)}
            disabled={actionLoading}
            className={classes.actionButton}
          >
            Alterar Nome
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setEditingDescription(!editingDescription)}
            disabled={actionLoading}
            className={classes.actionButton}
          >
            Descrição
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoCameraIcon />}
            onClick={handleUpdatePicture}
            disabled={actionLoading}
            className={classes.actionButton}
          >
            Foto
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<ExitToAppIcon />}
            onClick={handleLeaveGroup}
            disabled={actionLoading}
            className={classes.actionButton}
          >
            Sair
          </Button>
        </Box>

        {/* Group Description */}
        {editingDescription ? (
          <Box style={{ marginTop: 16 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Digite a descrição do grupo"
              disabled={actionLoading}
            />
            <Box style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleUpdateDescription}
                disabled={actionLoading}
              >
                Salvar
              </Button>
              <Button
                size="small"
                onClick={() => setEditingDescription(false)}
                disabled={actionLoading}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        ) : contact?.description ? (
          <>
            <Typography className={classes.sectionTitle}>
              Descrição
            </Typography>
            <Typography variant="body2" paragraph>
              {contact.description}
            </Typography>
            <Divider style={{ margin: "16px 0" }} />
          </>
        ) : null}

        {/* Participants */}
        <Typography className={classes.sectionTitle}>
          Participantes ({participants.length})
        </Typography>

        {loading ? (
          <Box className={classes.emptyState}>
            <CircularProgress size={40} />
          </Box>
        ) : participants.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              Nenhum participante encontrado
            </Typography>
          </Box>
        ) : (
          <List className={classes.participantsList}>
            {participants.map((participant, index) => (
              <React.Fragment key={participant.id}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar
                      src={participant.profilePicUrl}
                      alt={participant.name}
                    >
                      {participant.name?.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={participant.name || "Sem nome"}
                    secondary={participant.number || "Sem número"}
                  />
                </ListItem>
                {index < participants.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupInfoModal;
